import type { ConversionAttributionPayload, ServiceAreaSlug } from "@shared/attribution";
import { SERVICE_AREA_SLUGS } from "@shared/attribution";

const STORAGE_KEY = "partition-stalls-attribution-v1";

type AttributionSnapshot = Omit<ConversionAttributionPayload, "eventId" | "sourcePath">;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const pathPattern = /^\/[A-Za-z0-9/_-]*$/;
const domainPattern = /^[a-z0-9.-]+$/;

function cleanOptional(value: unknown, max: number) {
  if (typeof value !== "string") return undefined;
  const cleaned = value.trim().replace(/[\u0000-\u001F\u007F]/g, "").slice(0, max);
  return cleaned || undefined;
}

function createUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function serviceAreaFromPath(pathname: string): ServiceAreaSlug | undefined {
  const slug = pathname.match(/^\/service-areas\/([a-z-]+)\/?$/)?.[1];
  return SERVICE_AREA_SLUGS.find((candidate) => candidate === slug);
}

function serviceAreaFromSearch(search: string): ServiceAreaSlug | undefined {
  const value = new URLSearchParams(search).get("serviceArea");
  return SERVICE_AREA_SLUGS.find((candidate) => candidate === value);
}

function externalReferrerDomain(currentHostname: string) {
  if (!document.referrer) return undefined;
  try {
    const hostname = new URL(document.referrer).hostname.toLowerCase();
    return hostname && hostname !== currentHostname.toLowerCase() ? hostname : undefined;
  } catch {
    return undefined;
  }
}

function buildSnapshot(location: Location): AttributionSnapshot {
  const params = new URLSearchParams(location.search);
  return {
    sessionId: createUuid(),
    landingPath: location.pathname,
    serviceAreaSource: serviceAreaFromSearch(location.search) || serviceAreaFromPath(location.pathname),
    referrerDomain: externalReferrerDomain(location.hostname),
    utmSource: cleanOptional(params.get("utm_source"), 128),
    utmMedium: cleanOptional(params.get("utm_medium"), 128),
    utmCampaign: cleanOptional(params.get("utm_campaign"), 255),
    utmTerm: cleanOptional(params.get("utm_term"), 255),
    utmContent: cleanOptional(params.get("utm_content"), 255),
  };
}

function normalizeStoredSnapshot(value: unknown): AttributionSnapshot | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.sessionId !== "string" || !uuidPattern.test(candidate.sessionId)) return undefined;
  if (typeof candidate.landingPath !== "string" || !pathPattern.test(candidate.landingPath)) return undefined;
  const serviceAreaSource = SERVICE_AREA_SLUGS.find((slug) => slug === candidate.serviceAreaSource);
  const referrerCandidate = cleanOptional(candidate.referrerDomain, 253)?.toLowerCase();
  return {
    sessionId: candidate.sessionId,
    landingPath: candidate.landingPath,
    serviceAreaSource,
    referrerDomain: referrerCandidate && domainPattern.test(referrerCandidate) ? referrerCandidate : undefined,
    utmSource: cleanOptional(candidate.utmSource, 128),
    utmMedium: cleanOptional(candidate.utmMedium, 128),
    utmCampaign: cleanOptional(candidate.utmCampaign, 255),
    utmTerm: cleanOptional(candidate.utmTerm, 255),
    utmContent: cleanOptional(candidate.utmContent, 255),
  };
}

function getSnapshot(location: Location): AttributionSnapshot {
  try {
    const existing = sessionStorage.getItem(STORAGE_KEY);
    if (existing) {
      const normalized = normalizeStoredSnapshot(JSON.parse(existing));
      if (normalized) return normalized;
    }
    const snapshot = buildSnapshot(location);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    return snapshot;
  } catch {
    return buildSnapshot(location);
  }
}

export function initializeConversionAttribution() {
  if (typeof window !== "undefined") getSnapshot(window.location);
}

export function createConversionAttribution(sourcePath?: string): ConversionAttributionPayload {
  if (typeof window === "undefined") {
    const path = sourcePath || "/";
    return { eventId: createUuid(), sessionId: createUuid(), sourcePath: path, landingPath: path };
  }
  const snapshot = getSnapshot(window.location);
  return {
    ...snapshot,
    eventId: createUuid(),
    sourcePath: sourcePath && pathPattern.test(sourcePath) ? sourcePath : window.location.pathname,
    serviceAreaSource: serviceAreaFromSearch(window.location.search)
      || serviceAreaFromPath(window.location.pathname)
      || snapshot.serviceAreaSource,
  };
}
